import React, { useEffect, useState } from "react";
import { useQwanyx } from "qwanyx";
import Card from "./Card";

// Global shortcut registry that persists across renders and component visibility
const shortcutRegistry = {};

// Initialize the global keydown listener (runs only once)
if (typeof window !== 'undefined' && !window.__shortcutListenerInitialized) {
  window.addEventListener('keydown', (event) => {
    // Build shortcut string (e.g., "Alt 1")
    let shortcutString = '';
    
    if (event.altKey) shortcutString += 'Alt ';
    if (event.ctrlKey) shortcutString += 'Ctrl ';
    if (event.shiftKey) shortcutString += 'Shift ';
    
    shortcutString += event.key;
    
    // Check if this shortcut is registered
    if (shortcutRegistry[shortcutString]) {
      const { cardName, updateCardByName, debug } = shortcutRegistry[shortcutString];
      
      if (debug) {
        console.log(`Shortcut triggered: "${shortcutString}" for card ${cardName}`);
      }
      
      // Toggle visibility in registry directly
      updateCardByName(cardName, { isVisible: true });
      
      if (debug) {
        console.log(`Shortcut handler: Made card ${cardName} visible via global shortcut`);
      }
      
      // Prevent default browser behavior
      event.preventDefault();
    }
  });
  
  window.__shortcutListenerInitialized = true;
}

/**
 * CardContainer - A container component that adds visibility control 
 * and card identification to the Card component
 * 
 * @param {Object} props - The component props
 * @param {string} props._id - Card ID
 * @param {string} props.di - Domain ID
 * @param {string} props.ed - Entity data
 * @param {string} props.pa - Parent ID
 * @param {boolean} props.useGlobalContext - Whether to use the global context
 * @param {string} props.cardName - Name/key for this card instance (required for registry)
 * @param {boolean} props.debug - Whether to show debug information
 * @param {boolean} props.isVisible - Direct visibility control prop (optional)
 * @param {string} props.shortcut - Keyboard shortcut (e.g., "Alt 1") to toggle visibility
 * @returns {JSX.Element|null} - Card component or null if hidden
 */
export const CardContainer = ({ 
  _id, 
  di, 
  ed, 
  pa, 
  useGlobalContext = false,
  cardName, // This is now used as the key for the card registry
  debug = false,
  isVisible = true, // Add direct prop for visibility with default true
  shortcut, // New prop for keyboard shortcut
  ...otherProps  // Pass any other props to the Card component
}) => {
  // Get state and functions from QwanyxProvider
  const { 
    // Card visibility
    isCardVisible: globalVisibility,
    
    // Registry functions
    registerCard,
    getCardByName,
    updateCardByName,
    
    // Selected card functions
    updateSelectedCard,
    
    // Other context values
    selectedCard
  } = useQwanyx();
  
  // Local state to track this card's properties 
  const [localCardState, setLocalCardState] = useState({
    _id,
    di,
    ed,
    pa,
    isVisible,
    cardName
  });
  
  // Register the card with the provider when it mounts or when key props change
  useEffect(() => {
    if (!cardName) {
      if (debug) {
        console.log("CardContainer: No cardName provided, cannot register with registry");
      }
      return;
    }
    
    // Prepare card data for registration
    const cardData = {
      _id,
      di,
      ed,
      pa,
      isVisible,
      cardName,
      registeredAt: Date.now()
    };
    
    // Register with the global registry
    registerCard(cardName, cardData);
    
    // Update local state
    setLocalCardState(cardData);
    
    if (debug) {
      console.log(`CardContainer: Registered card ${cardName} with registry`);
    }
    
    // If useGlobalContext is true, also update the selected card
    if (useGlobalContext && _id && di) {
      // Update provider with card data and identifier
      updateSelectedCard({
        _id,
        di,
        ed,
        pa,
        cardName,
        isVisible
      });

      if (debug) {
        console.log(`CardContainer: Set as selected card: ${cardName}`);
      }
    }
  }, [_id, di, ed, pa, cardName, isVisible, useGlobalContext, registerCard, updateSelectedCard, debug]);
  
  // Listen for updates to this card in the registry
  useEffect(() => {
    if (!cardName) return;
    
    // Get current state from registry
    const registryCardData = getCardByName(cardName);
    
    if (registryCardData) {
      // Update local state when registry changes
      setLocalCardState(prev => {
        // Only update if there's an actual change
        if (
          prev._id !== registryCardData._id ||
          prev.di !== registryCardData.di ||
          prev.ed !== registryCardData.ed ||
          prev.pa !== registryCardData.pa ||
          prev.isVisible !== registryCardData.isVisible
        ) {
          if (debug) {
            console.log(`CardContainer: Updated from registry for ${cardName}`, registryCardData);
          }
          return registryCardData;
        }
        return prev;
      });
    }
  }, [cardName, getCardByName, debug]);
  
  // LOCAL toggle function for when the card is rendered
  const toggleVisibility = () => {
    // Get latest card data to ensure we have current state
    const cardData = getCardByName(cardName);
    const newVisibility = !(cardData?.isVisible);
    
    // Update in registry
    updateCardByName(cardName, { isVisible: newVisibility });
    
    if (debug) {
      console.log(`CardContainer: Toggled visibility for ${cardName} to ${newVisibility}`);
    }
  };
  
  // Key effect: Register shortcut when component mounts AND keep updateCardByName reference for global handler
  useEffect(() => {
    if (!shortcut || !cardName) return;
    
    // Register in the global shortcut registry
    shortcutRegistry[shortcut] = {
      cardName,
      updateCardByName, // Store the function reference for the global handler
      debug
    };
    
    if (debug) {
      console.log(`CardContainer: Registered shortcut "${shortcut}" for card ${cardName}`);
    }
    
    // Set up a local keydown handler for when the card is visible
    const handleKeyDown = (event) => {
      let keyCombo = '';
      
      if (event.altKey) keyCombo += 'Alt ';
      if (event.ctrlKey) keyCombo += 'Ctrl ';
      if (event.shiftKey) keyCombo += 'Shift ';
      
      keyCombo += event.key;
      
      if (keyCombo === shortcut) {
        if (debug) {
          console.log(`CardContainer: Local shortcut handler triggered for ${cardName}`);
        }
        
        // Call the toggle function
        toggleVisibility();
        
        // Prevent default browser behavior
        event.preventDefault();
      }
    };
    
    // Add local handler for when component is rendered
    window.addEventListener('keydown', handleKeyDown);
    
    // Clean up both global and local handlers when unmounted
    return () => {
      delete shortcutRegistry[shortcut];
      window.removeEventListener('keydown', handleKeyDown);
      
      if (debug) {
        console.log(`CardContainer: Cleaned up shortcut handlers for ${cardName}`);
      }
    };
  }, [shortcut, cardName, updateCardByName, toggleVisibility, debug]);
  
  // Check if the card should be visible
  const shouldRender = useGlobalContext 
    ? (globalVisibility && localCardState.isVisible !== false) 
    : (localCardState.isVisible !== false);
  
  // Don't render if not visible
  if (!shouldRender) {
    if (debug) {
      console.log(`CardContainer: Card ${cardName || _id} is hidden`);
    }
    return null;
  }
  
  // Render the Card with current state
  return (
    <Card
      _id={localCardState._id}
      di={localCardState.di}
      ed={localCardState.ed}
      pa={localCardState.pa}
      useGlobalContext={useGlobalContext}
      debug={debug}
      {...otherProps}
    />
  );
};

export default CardContainer;